import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import {
    CLIENTES, ROTULO_OUTROS, corTextoSobre, formatarNumero, formatarPercentual, formatarPercentualCurto, participacaoPorCliente, temaGrafico,
} from "./analiseUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const MIN_ROTULO = 0.06; // segmentos menores que 6% ficam só no tooltip
const ALTURA_BARRA = 40;

const comAlfa = (hex, alfa) => {
    const valor = hex.replace("#", "");
    const [r, g, b] = [0, 2, 4].map(i => parseInt(valor.substring(i, i + 2), 16));
    return `rgba(${r}, ${g}, ${b}, ${alfa})`;
};

/** % escrito dentro de cada segmento largo o bastante (rótulo direto). */
const rotuloNoSegmento = {
    id: "rotuloNoSegmento",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        chart.data.datasets.forEach((dataset, di) => {
            const meta = chart.getDatasetMeta(di);
            if (meta.hidden) return;
            meta.data.forEach((segmento, i) => {
                const valor = dataset.data[i];
                if (!valor || valor < MIN_ROTULO || dataset.esmaecido[i]) return;
                const { x, y, base } = segmento.getProps(["x", "y", "base"], true);
                if (Math.abs(x - base) < 34) return;
                ctx.fillStyle = corTextoSobre(dataset.corBase);
                ctx.fillText(formatarPercentualCurto(valor), (x + base) / 2, y);
            });
        });
        ctx.restore();
    },
};

/** Fichas acionadas do cliente escritas à direita de cada barra. */
const fichasNaPonta = {
    id: "fichasNaPonta",
    afterDatasetsDraw(chart, args, opcoes) {
        const { ctx, chartArea } = chart;
        const meta = chart.getDatasetMeta(0);
        ctx.save();
        ctx.font = "600 11px Roboto, Helvetica, Arial, sans-serif";
        ctx.fillStyle = opcoes.cor;
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        meta.data.forEach((segmento, i) => {
            const { y } = segmento.getProps(["y"], true);
            ctx.fillText(`${formatarNumero(opcoes.fichas[i])} fichas`, chartArea.right + 10, y);
        });
        ctx.restore();
    },
};

/**
 * Participação por cliente: uma barra por cliente (soma 100%), dividida entre as pessoas que o atenderam.
 * As 4 pessoas com mais atendimentos têm cor própria (a mesma da rosca); as demais somam em "Outros".
 * Com uma pessoa filtrada, a barra mostra a fatia dela contra "Demais".
 */
export default function GraficoEsforco({ blocos, cores, fichasPorCliente, selecionado, clienteDestaque }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);

    const participacoes = CLIENTES.map(cliente => participacaoPorCliente(blocos, cliente));
    const percentualDe = (idxCliente, nomes) => participacoes[idxCliente].partes
        .filter(p => nomes.includes(p.nome))
        .reduce((acc, p) => acc + p.percentual, 0);
    const quantidadeDe = (idxCliente, nomes) => participacoes[idxCliente].partes
        .filter(p => nomes.includes(p.nome))
        .reduce((acc, p) => acc + p.quantidade, 0);

    const todosNomes = blocos.map(b => b.nome);
    const series = selecionado
        ? [
            { rotulo: selecionado, cor: cores.cor(selecionado), nomes: [selecionado] },
            { rotulo: "Demais", cor: cores.corOutros, nomes: todosNomes.filter(n => n !== selecionado) },
        ]
        : [
            ...cores.destaques.map(nome => ({ rotulo: nome, cor: cores.cor(nome), nomes: [nome] })),
            { rotulo: ROTULO_OUTROS, cor: cores.corOutros, nomes: todosNomes.filter(n => !cores.destaques.includes(n)) },
        ];

    const data = {
        labels: CLIENTES,
        datasets: series
            .filter(serie => serie.nomes.length > 0)
            .map(serie => {
                const esmaecido = CLIENTES.map(c => !!clienteDestaque && c !== clienteDestaque);
                return {
                    label: serie.rotulo,
                    nomes: serie.nomes,
                    corBase: serie.cor,
                    esmaecido,
                    data: CLIENTES.map((c, i) => percentualDe(i, serie.nomes)),
                    backgroundColor: esmaecido.map(e => (e ? comAlfa(serie.cor, 0.25) : serie.cor)),
                    borderColor: tema.superficie,
                    borderWidth: { left: 1, right: 1 },
                    borderSkipped: false,
                    borderRadius: 3,
                    barThickness: 26,
                };
            }),
    };

    const options = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 80 } },
        plugins: {
            legend: {
                position: "top",
                align: "start",
                labels: {
                    color: tema.texto,
                    usePointStyle: true,
                    pointStyle: "circle",
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 14,
                    font: { size: 12 },
                    // Cor cheia na legenda mesmo quando o cliente em destaque esmaece as demais barras
                    generateLabels: (chart) => chart.data.datasets.map((dataset, i) => ({
                        text: dataset.label,
                        fillStyle: dataset.corBase,
                        strokeStyle: dataset.corBase,
                        fontColor: tema.texto,
                        pointStyle: "circle",
                        hidden: !chart.isDatasetVisible(i),
                        datasetIndex: i,
                    })),
                },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const quantidade = quantidadeDe(ctx.dataIndex, ctx.dataset.nomes);
                        const total = participacoes[ctx.dataIndex].total;
                        return `${ctx.dataset.label}: ${formatarPercentual(ctx.parsed.x)} (${formatarNumero(quantidade)} de ${formatarNumero(total)} fichas acionadas mês a mês)`;
                    },
                },
            },
            fichasNaPonta: { cor: tema.texto, fichas: CLIENTES.map(c => fichasPorCliente[c] || 0) },
        },
        scales: {
            x: {
                stacked: true,
                min: 0,
                max: 1,
                grid: { color: tema.grade },
                border: { display: false },
                ticks: { color: tema.textoSecundario, font: { size: 11 }, stepSize: 0.2, callback: (v) => formatarPercentualCurto(v) },
            },
            y: {
                stacked: true,
                grid: { display: false },
                border: { display: false },
                ticks: {
                    color: tema.texto,
                    font: (ctx) => ({ size: 12, weight: clienteDestaque && CLIENTES[ctx.index] === clienteDestaque ? "bold" : "600" }),
                },
            },
        },
    };

    return (
        <MDBox height={`${CLIENTES.length * ALTURA_BARRA + 90}px`}>
            <Bar data={data} options={options} plugins={[rotuloNoSegmento, fichasNaPonta]} />
        </MDBox>
    );
}
